/**
 * Which drafted shifts a breach is about.
 *
 * The dry run reports breaches the way the evaluators do: a guard, a roster
 * date, and the duties of the shift the breach was pinned on. That shift is
 * not always the drafted one -- a short rest is reported against whichever
 * neighbour sits on the roster being judged, and an average against the day
 * it tips over. So the search widens in steps until it finds drafted work of
 * that guard to take back.
 */

import { exclusionKey } from './planner';
import { toDayNumber } from './time';
import type { PlanViolation, PlannedAssignment } from './types';

/** Breaches judged over a trailing window: the work that tipped it is on or before the day reported. */
const TRAILING_WINDOW_CODES = new Set([
  'max_average_weekly_hours',
  'night_work_average_weekly_hours',
  'night_work_average_24h_hours',
]);

function blamed(violation: PlanViolation, drafted: PlannedAssignment[]): PlannedAssignment[] {
  if (drafted.length === 0) return [];

  const named = drafted.filter((assignment) => violation.dutySlotIds.includes(assignment.block.slotId));
  if (named.length > 0) return named;

  const day = toDayNumber(violation.slotDate);
  const distance = (assignment: PlannedAssignment): number => Math.abs(toDayNumber(assignment.block.slotDate) - day);

  if (TRAILING_WINDOW_CODES.has(violation.code)) {
    const upToDay = drafted.filter((assignment) => toDayNumber(assignment.block.slotDate) <= day);
    if (upToDay.length > 0) {
      const latest = Math.max(...upToDay.map((assignment) => toDayNumber(assignment.block.slotDate)));
      return upToDay.filter((assignment) => toDayNumber(assignment.block.slotDate) === latest);
    }
  }

  const nearest = Math.min(...drafted.map(distance));
  return drafted.filter((assignment) => distance(assignment) === nearest);
}

/**
 * The guard-and-shift pairings to rule out before planning again. Empty only
 * when the breaches name nobody the draft gave work to.
 */
export function blameAssignments(violations: PlanViolation[], assignments: PlannedAssignment[]): Set<string> {
  const byGuard = new Map<string, PlannedAssignment[]>();
  assignments.forEach((assignment) => {
    const list = byGuard.get(assignment.guardId) ?? [];
    list.push(assignment);
    byGuard.set(assignment.guardId, list);
  });

  const excluded = new Set<string>();
  violations.forEach((violation) => {
    if (!violation.guardId) return;
    blamed(violation, byGuard.get(violation.guardId) ?? []).forEach((assignment) => {
      excluded.add(exclusionKey(assignment.guardId, assignment.block.key));
    });
  });
  return excluded;
}

/** Guards named in a set of breaches. */
export function namedGuardIds(violations: PlanViolation[]): Set<string> {
  return new Set(violations.flatMap((violation) => (violation.guardId ? [violation.guardId] : [])));
}
